import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';
import { SICallTypes } from 'src/app/features/service-insights/types/service-insight-types';
import { FiltersStore } from 'src/app/core/stores/filters.store';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { ServiceInsightsService } from '../services/service-insights-service.service';
import { ServiceInsightsDetailsKpiCardsData } from '../types/service-insight-types';

/**
 * Store for managing the state and data of the Service Insights Details feature.
 *
 * Responsibilities
 * 1. Manage the state of the selected Service Insights metric.
 * 2. Own and expose feature state through observable streams.
 * 3. Persist store state across the application lifecycle to
 * avoid unnecessary API requests across navigation.
 *
 * Non-responsibilities
 * 1. Coordinate complex business workflows (use an orchestrator).
 * 2. Handle UI rendering or presentation concerns (leave that to the components).
 *
 * TODO:
 * As this feature grows, consider introducing a ServiceInsightsDetailsOrchestrator or smaller scoped orchestrators (recommended).
 * The orchestrator would coordinate data-loading workflows, invoke services, and
 * update one or more stores in response to filter or metric changes. For now, the
 * store remains lightweight, and keeping the data-loading logic here avoids
 * unnecessary architectural complexity until additional state and workflows are introduced.
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceInsightsDetailsStore {
  // Data for the service insights details feature is driven by the selected service insights metric.
  private readonly selectedServiceInsightsMetric: BehaviorSubject<SICallTypes> =
    new BehaviorSubject<SICallTypes>(SICallTypes.UntrackedCalls);
  readonly selectedServiceInsightsMetric$ =
    this.selectedServiceInsightsMetric.asObservable();

  // KPI cards data for the service insights details feature.
  private readonly _kpiCardsData: BehaviorSubject<
    ServiceInsightsDetailsKpiCardsData[]
  > = new BehaviorSubject<ServiceInsightsDetailsKpiCardsData[]>([]);
  readonly kpiCardsData$ = this._kpiCardsData.asObservable();

  constructor(private _serviceInsightsService: ServiceInsightsService) {}

  /**
   * Sets the selected service insights metric and triggers the loading of KPI cards data.
   * @param serviceMetric The service insights metric to select.
   */
  setSelectedServiceInsightsMetric(serviceMetric: SICallTypes) {
    this.selectedServiceInsightsMetric.next(serviceMetric);
    this._kpiCardsData.next([]); // Clear the KPI cards data before loading new data, triggers the skeleton loader
    void this.loadKpiCardsData();
  }

  /**
   * Loads the KPI cards data for the currently selected service insights metric.
   * This method fetches the data from the service insights service and updates
   * the corresponding BehaviorSubject.
   */
  async loadKpiCardsData() {
    const selectedMetric = this.selectedServiceInsightsMetric.getValue();

    const kpiCardsData =
      await this._serviceInsightsService.getServiceDetailsKpiData(
        selectedMetric,
      );
    this._kpiCardsData.next(kpiCardsData ?? []);
  }

  get kpiCardsDataValue(): ServiceInsightsDetailsKpiCardsData[] {
    return this._kpiCardsData.value;
  }

  invalidateStore(): void {
    this.selectedServiceInsightsMetric.next(SICallTypes.UntrackedCalls);

    this._kpiCardsData.next([]);
  }
}
